import { createSlice, PayloadAction, PrepareAction } from '@reduxjs/toolkit'
import { PlaylistSort } from 'constants/enums'

interface Playlist {
  playlistid: string | number
  [key: string]: unknown
}

interface PlaylistListQuery {
  searchToken: string
  sort: string
}

interface PlayListState {
  activeRequestId: string | null
  page: number
  requestedPage: number
  listRevision: number
  listQuery: PlaylistListQuery
  totalItemsCount: number | null
  loading: boolean
  error: unknown
  releases: unknown[]
  playLists: Playlist[]
  playlistAbstracts: Playlist[]
  songsToAdd: unknown[]
  playlistUnderDisplay: Playlist | null
  playlistUnderEditing: Playlist | null
  searchToken: string
  sort: string
}

const initialState: PlayListState = {
  activeRequestId: null,
  page: 0,
  requestedPage: 1,
  listRevision: 0,
  listQuery: {
    searchToken: '',
    sort: PlaylistSort.newest.value,
  },
  totalItemsCount: null,
  loading: false,
  error: null,
  releases: [],
  playLists: [],
  playlistAbstracts: [],
  songsToAdd: [],
  playlistUnderDisplay: null,
  playlistUnderEditing: null,
  searchToken: '',
  sort: PlaylistSort.newest.value,
}

const playListSlice = createSlice({
  name: 'playList',
  initialState,
  reducers: {
    startLoadingPlaylist: (state) => {
      state.loading = true
      state.error = null
    },
    listRequestStarted: (
      state,
      action: PayloadAction<{
        page: number
        query: PlaylistListQuery
        requestId: string
      }>
    ) => {
      const { page, query, requestId } = action.payload

      state.activeRequestId = requestId
      state.requestedPage = page
      state.loading = true
      state.error = null

      if (page === 1) {
        state.listQuery = query
        state.listRevision = (state.listRevision ?? 0) + 1
      } else if (!state.listQuery) {
        state.listQuery = query
      }
    },
    setSongsToAdd: (state, action: PayloadAction<unknown[]>) => {
      state.songsToAdd = action.payload
    },
    setPlaylistAbstracts: (state, action: PayloadAction<Playlist[]>) => {
      state.playlistAbstracts = action.payload
    },
    loadingPlaylistSuccess: {
      prepare(
        playlists: Playlist[],
        page: number,
        totalItemsCount: number,
        requestId?: string
      ) {
        return {
          payload: {
            playlists,
            page,
            totalItemsCount,
            ...(requestId ? { requestId } : {}),
          },
        }
      },
      reducer(
        state,
        action: PayloadAction<{
          playlists: Playlist[]
          page: number
          totalItemsCount: number
          requestId?: string
        }>
      ) {
        const { playlists, page, requestId, totalItemsCount } = action.payload
        const activeRequestId = state.activeRequestId ?? null

        if (
          (requestId && requestId !== activeRequestId) ||
          (!requestId && activeRequestId)
        ) {
          return
        }

        if (page === 1) {
          state.playLists = playlists
        } else if (page === state.page + 1) {
          state.playLists = state.playLists.concat(playlists)
        } else {
          if (requestId) {
            state.loading = false
            state.activeRequestId = null
          }
          return
        }
        state.page = page
        state.totalItemsCount = totalItemsCount
        state.loading = false
        state.error = null
        state.activeRequestId = null
      },
    },
    loadingPlaylistFail: {
      prepare(error: unknown, requestId?: string) {
        return {
          payload: requestId ? { error, requestId } : error,
        }
      },
      reducer(state, action: PayloadAction<unknown>) {
        const trackedFailure =
          action.payload !== null &&
          typeof action.payload === 'object' &&
          'requestId' in action.payload &&
          'error' in action.payload
            ? (action.payload as { error: unknown; requestId: string })
            : null
        const requestId = trackedFailure?.requestId
        const activeRequestId = state.activeRequestId ?? null

        if (
          (requestId && requestId !== activeRequestId) ||
          (!requestId && activeRequestId)
        ) {
          return
        }

        state.error = trackedFailure ? trackedFailure.error : action.payload
        state.loading = false
        state.activeRequestId = null
      },
    },
    setPlaylists: (state, action: PayloadAction<Playlist[]>) => {
      state.playLists = action.payload
    },
    createPlayList: (state, action: PayloadAction<Playlist>) => {
      state.playLists.push({ playList: action.payload } as unknown as Playlist)
    },
    createPlaylistSuccess: (state, action: PayloadAction<Playlist>) => {
      const playlist = action.payload

      state.playLists = [
        playlist,
        ...state.playLists.filter(
          (item) => item.playlistid !== playlist.playlistid
        )
      ]
      state.playlistAbstracts = [
        playlist,
        ...state.playlistAbstracts.filter(
          (item) => item.playlistid !== playlist.playlistid
        )
      ]
      state.totalItemsCount = (state.totalItemsCount || 0) + 1
    },
    // Confirmation loading/error state is local; keep list requests independent.
    deletePlaylistStart: (state) => state,
    deletePlaylistSuccess: (state, action: PayloadAction<Playlist>) => {
      const { playlistid } = action.payload
      const isRemovedPlaylist = (item: Playlist | null) =>
        String(item?.playlistid) === String(playlistid)
      const isInCurrentList = state.playLists.some(isRemovedPlaylist)

      // Responses from before this mutation must not restore a deleted card.
      // The following page-one refresh establishes a fresh request identity.
      state.activeRequestId = null
      state.loading = false
      state.error = null
      state.page = 0
      state.requestedPage = 1
      state.playLists = state.playLists.filter(
        (item) => !isRemovedPlaylist(item)
      )
      state.playlistAbstracts = state.playlistAbstracts.filter(
        (item) => !isRemovedPlaylist(item)
      )

      if (isInCurrentList && typeof state.totalItemsCount === 'number') {
        state.totalItemsCount = Math.max(0, state.totalItemsCount - 1)
      }
      if (isRemovedPlaylist(state.playlistUnderDisplay)) {
        state.playlistUnderDisplay = null
      }
      if (isRemovedPlaylist(state.playlistUnderEditing)) {
        state.playlistUnderEditing = null
      }
    },
    deletePlaylistError: (state) => state,
    deleteSongFromPlaylist: {
      prepare(song: unknown, playlist: unknown) {
        return { payload: { song, playlist } }
      },
      reducer() {
        // deprecated/unused
      },
    },
    updatePlaylist: (state, action: PayloadAction<Playlist>) => {
      const playlist = action.payload
      state.playLists = state.playLists.map((item) =>
        item.playlistid === playlist.playlistid ? playlist : item
      )
      state.playlistAbstracts = state.playlistAbstracts.map((item) =>
        item.playlistid === playlist.playlistid ? playlist : item
      )
      if (state.playlistUnderDisplay?.playlistid === playlist.playlistid) {
        state.playlistUnderDisplay = playlist
      }
      if (state.playlistUnderEditing?.playlistid === playlist.playlistid) {
        state.playlistUnderEditing = playlist
      }
    },
    setPlaylistUnderDisplay: (state, action: PayloadAction<Playlist | null>) => {
      state.playlistUnderDisplay = action.payload
    },
    setPlaylistUnderEditing: (state, action: PayloadAction<Playlist | null>) => {
      state.playlistUnderEditing = action.payload
    },
    addTracksStart: (state) => {
      state.loading = true
    },
    addTracksSuccess: (state) => {
      state.loading = false
    },
    addTracksFail: (state) => {
      state.loading = false
    },
    renameStart: (state) => {
      state.loading = true
    },
    renameSuccess: (state, action: PayloadAction<Playlist>) => {
      state.loading = false
    },
    renameFail: (state) => {
      state.loading = false
    },
    updateLoadingPlaylist: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    pinPlaylist: (state, action: PayloadAction<Playlist>) => {
      state.playLists = state.playLists.map((item) =>
        item.playlistid === action.payload.playlistid ? action.payload : item
      )
    },
    unpinPlaylist: (state, action: PayloadAction<Playlist>) => {
      state.playLists = state.playLists.map((item) =>
        item.playlistid === action.payload.playlistid ? action.payload : item
      )
    },
    updateSearchToken: (state, action: PayloadAction<string>) => {
      state.searchToken = action.payload
    },
    updateSort: (state, action: PayloadAction<string>) => {
      state.sort = action.payload
    },
    reset: () => initialState,
  },
})

export const playListActions = playListSlice.actions
export const playListReducer = playListSlice.reducer
export default playListActions
